import type { ApiDashboardElement } from '@highimpact-frontend/api-types';
import {
  DisplayerTypeEnum,
  isDisplayerTypeSuccess,
  type DisplayerType,
} from '@highimpact-frontend/displayer-types';

import {
  supportsElementMeasureRequest,
} from '../../../../runtime/elementMeasureSupport';

function isTimelinePlotElement(element: ApiDashboardElement): boolean {
  const properties = element.properties as
    | { state_type?: string; plot_type?: string }
    | undefined;

  return (
    element.element_type === 'plot' &&
    properties?.state_type === 'configured_plot' &&
    properties.plot_type === 'timeseries'
  );
}

function isScatterPlotElement(element: ApiDashboardElement): boolean {
  const properties = element.properties as
    | { state_type?: string; plot_type?: string }
    | undefined;

  return (
    element.element_type === 'plot' &&
    properties?.state_type === 'configured_plot' &&
    properties.plot_type === 'scatter'
  );
}

function isConfiguredTableElement(element: ApiDashboardElement): boolean {
  return (
    element.element_type === 'table' &&
    (element.properties as { state_type?: string }).state_type ===
      'configured_table'
  );
}

function isConfiguredTableByCampaignElement(
  element: ApiDashboardElement
): boolean {
  return (
    element.element_type === 'tablebycampaign' &&
    (element.properties as { state_type?: string }).state_type ===
      'configured_table_by_campaign'
  );
}

/** Returns true when a displayer slice entry belongs to the given element. */
export function displayerMatchesElement(
  displayer: DisplayerType,
  element: ApiDashboardElement
): boolean {
  if (!isDisplayerTypeSuccess(displayer)) {
    return false;
  }

  if (isTimelinePlotElement(element)) {
    return displayer.type === DisplayerTypeEnum.timeseries;
  }
  if (isScatterPlotElement(element)) {
    return displayer.type === DisplayerTypeEnum.scatter;
  }
  if (isWindrosePlotElement(element)) {
    return displayer.type === DisplayerTypeEnum.rose_wind;
  }
  if (isBarPlotElement(element)) {
    return displayer.type === DisplayerTypeEnum.histogram;
  }
  if (isOmbrothermicPlotElement(element)) {
    return displayer.type === DisplayerTypeEnum.ombrothermic;
  }
  if (isConfiguredTableElement(element)) {
    return displayer.type === DisplayerTypeEnum.table;
  }
  if (isConfiguredTableByCampaignElement(element)) {
    return displayer.type === DisplayerTypeEnum.tablebycampaign;
  }

  return (
    displayer.type !== DisplayerTypeEnum.timeseries &&
    displayer.type !== DisplayerTypeEnum.scatter
  );
}

/** Plot widgets load measures from element JSON, not Redux datasets. */
export function usesElementMeasureTransport(
  element: ApiDashboardElement
): boolean {
  return supportsElementMeasureRequest(element);
}

function isWindrosePlotElement(element: ApiDashboardElement): boolean {
  const properties = element.properties as
    | { state_type?: string; plot_type?: string }
    | undefined;

  return (
    element.element_type === 'plot' &&
    properties?.state_type === 'configured_plot' &&
    properties.plot_type === 'windrose'
  );
}

function isBarPlotElement(element: ApiDashboardElement): boolean {
  const properties = element.properties as
    | { state_type?: string; plot_type?: string }
    | undefined;

  return (
    element.element_type === 'plot' &&
    properties?.state_type === 'configured_plot' &&
    properties.plot_type === 'bar'
  );
}

function isOmbrothermicPlotElement(element: ApiDashboardElement): boolean {
  const properties = element.properties as
    | { state_type?: string; plot_type?: string }
    | undefined;

  return (
    element.element_type === 'plot' &&
    properties?.state_type === 'configured_plot' &&
    properties.plot_type === 'ombrothermic'
  );
}

/** Chooses the plot/table renderer from element JSON first, then displayer type. */
export function resolveDisplayerRendererType(
  element: ApiDashboardElement,
  displayer: DisplayerType
): DisplayerType['type'] {
  const fromElement = resolveRendererTypeFromElement(element);
  if (fromElement !== 'DisplayerTypeError') {
    return fromElement;
  }

  return isDisplayerTypeSuccess(displayer) ? displayer.type : 'DisplayerTypeError';
}

/** Chooses the plot/table renderer from element JSON only. */
export function resolveRendererTypeFromElement(
  element: ApiDashboardElement
): DisplayerType['type'] {
  if (isTimelinePlotElement(element)) {
    return DisplayerTypeEnum.timeseries;
  }
  if (isScatterPlotElement(element)) {
    return DisplayerTypeEnum.scatter;
  }
  if (isWindrosePlotElement(element)) {
    return DisplayerTypeEnum.rose_wind;
  }
  if (isBarPlotElement(element)) {
    return DisplayerTypeEnum.histogram;
  }
  if (isOmbrothermicPlotElement(element)) {
    return DisplayerTypeEnum.ombrothermic;
  }
  if (isConfiguredTableElement(element)) {
    return DisplayerTypeEnum.table;
  }
  if (isConfiguredTableByCampaignElement(element)) {
    return DisplayerTypeEnum.tablebycampaign;
  }

  return 'DisplayerTypeError';
}
